import {
  ToggleButtonGroup as MuiToggleButtonGroup,
  ToggleButtonGroupProps as MuiToggleButtonGroupProps,
  ToggleButton,
} from '@mui/material';
import {
  Control,
  Controller,
  FieldValues,
  Path,
  RegisterOptions,
} from 'react-hook-form';

import { Option } from '@/types/components/common';

import { getDataTestId } from '@/utils/browser';

export type ToggleButtonGroupProps = {
  value: MuiToggleButtonGroupProps['value'];
  onChange: MuiToggleButtonGroupProps['onChange'];
  options: Option[];
  name?: string;
} & Omit<MuiToggleButtonGroupProps, 'value' | 'onChange' | 'children'>;

export function ToggleButtonGroup({
  value,
  onChange,
  options,
  name,
  ...props
}: ToggleButtonGroupProps) {
  return (
    <MuiToggleButtonGroup
      {...getDataTestId(`${name}-toggle-group`)}
      size="small"
      value={value}
      exclusive
      onChange={onChange}
      {...props}
    >
      {options.map(option => (
        <ToggleButton
          key={`toggle-${option.value}`}
          {...getDataTestId(`${name}-option-${option.value}`)}
          value={option.value}
        >
          {option.text}
        </ToggleButton>
      ))}
    </MuiToggleButtonGroup>
  );
}

export type ToggleGroupProps<FormPayload extends FieldValues> = {
  options: Option[];
  control?: Control<FormPayload>;
  name?: Path<FormPayload>;
  rules?: Omit<
    RegisterOptions<FormPayload, Path<FormPayload>>,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  >;
} & MuiToggleButtonGroupProps;

export function ToggleGroup<FormPayload extends FieldValues>({
  value,
  onChange,
  options,
  name,
  control,
  rules,
  ...props
}: ToggleGroupProps<FormPayload>) {
  if (control && name) {
    return (
      <Controller
        rules={rules}
        control={control}
        name={name}
        render={({ field: { onChange, value } }) => (
          <ToggleButtonGroup
            value={value}
            onChange={onChange}
            options={options}
            name={name as string}
            {...props}
          />
        )}
      />
    );
  }

  return (
    <ToggleButtonGroup
      value={value}
      onChange={onChange}
      options={options}
      name={name as string}
      {...props}
    />
  );
}
